import { useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { Image } from "primereact/image";

import { pinAnswersResponse, refreshPrompt } from "services";
import {
  errorMessaggeAi,
  labelAnswerLocker,
  labelCopy,
  labelForward,
  labelRefresh,
  labelReport,
  labelSave,
  snackbarLimitRefershSubTitle,
  snackbarLimitRefershTitle,
  snackbarSeverityInfo,
} from "_constants";
import { useAppDispatch } from "store/store";
import { Answer } from "interfaces";
import {
  setRefreshAnswer,
  setRepliedAnswer,
  setWaitMessage,
  setNewMessage,
  setActionAnswer,
} from "store/slicer/GestioneChatSlice";
import { useSnackbar } from "providers/SnackbarProvider";
import {
  resetCurrentAnswer,
  resetLoadedStatus,
} from "store/slicer/GestioneSaveAnswerSlice";
import { ModalSendAnswerFeedback } from "components/modals";
import iconForward from "assets/icons/icon_forward.svg";

export const ActionsAnswer = ({
  questionId,
  answer,
  projectId,
  numberAnswerSelected,
  lenghtAnswers,
  callback,
  isLast,
  showActions,
  isDisabled,
}: {
  questionId: string;
  answer: Answer;
  projectId: string;
  numberAnswerSelected: number;
  lenghtAnswers: number;
  callback: (answerSelected: number) => void;
  isLast: boolean;
  showActions: boolean;
  isDisabled?: boolean;
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const dispatch = useAppDispatch();
  const { showSnackbar } = useSnackbar();

  const copyText = () => {
    const textArea = document.createElement("textarea");
    textArea.value = answer?.message;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Unable to copy to clipboard", err);
    }
    document.body.removeChild(textArea);
  };

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(answer?.message).then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 1000);
      });
    } else {
      copyText();
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 1000);
    }
  };

  const saveAnswerResponse = () => {
    pinAnswersResponse({
      projectId: projectId,
      pin: !answer?.isSaved,
      answersIds: [answer?.id],
    })
      .then((_) => {
        dispatch(
          setActionAnswer({
            idQuestion: questionId,
            idAnswer: answer.id,
            isSaved: !answer.isSaved,
          })
        );
        dispatch(resetLoadedStatus());
      })
      .catch((_) => {});
  };

  const refreshAnswer = () => {
    if (lenghtAnswers === 10) {
      showSnackbar({
        severity: snackbarSeverityInfo,
        detail: snackbarLimitRefershTitle,
        summary: snackbarLimitRefershSubTitle,
        life: 5000,
      });
    } else {
      dispatch(setWaitMessage(true));
      callback(lenghtAnswers);
      refreshPrompt({ questionId, projectId })
        .then((res) => {
          dispatch(setRefreshAnswer(res.data));
        })
        .catch((_) => {
          dispatch(setRefreshAnswer({ message: errorMessaggeAi }));
        })
        .finally(() => {
          dispatch(resetCurrentAnswer());
          dispatch(setNewMessage(true));
          dispatch(setWaitMessage(false));
        });
    }
  };

  const items = [
    {
      name: labelSave,
      icon: answer?.isSaved ? PrimeIcons.BOOKMARK_FILL : PrimeIcons.BOOKMARK,
      command: saveAnswerResponse,
    },
    {
      name: labelCopy,
      icon: copySuccess ? PrimeIcons.CHECK : PrimeIcons.COPY,
      command: handleCopy,
    },
    !isDisabled && {
      name: labelReport,
      icon: answer?.hasFeedback
        ? "pi pi-thumbs-down-fill"
        : PrimeIcons.THUMBS_DOWN,
      command: () => {
        if (!answer?.hasFeedback) {
          setShowFeedbackModal(true);
        }
      },
    },
    isLast && !isDisabled && {
      name: labelRefresh,
      icon: PrimeIcons.REFRESH,
      command: refreshAnswer,
    },
    !isDisabled &&{
      name: labelForward,
      icon: iconForward,
      command: () => {
        dispatch(setRepliedAnswer(answer?.message));
      },
    },
    isDisabled && {
      name: labelAnswerLocker,
      icon: PrimeIcons.LOCK,
      command: () => { },
    },
  ].filter(Boolean);

  return (
    <>
      <div className="flex-container ">
        {lenghtAnswers > 1 && (
          <div className="flex-container align-center ">
            <Button
              icon={PrimeIcons.ANGLE_LEFT}
              className="p-button-rounded p-button-text height-px-28 width-px-28"
              severity="secondary"
              disabled={numberAnswerSelected + 1 === 1}
              onClick={() => callback(numberAnswerSelected - 1)}
            />
            <span className="text-base-regular ">{`${
              numberAnswerSelected + 1
            }/${lenghtAnswers}`}</span>
            <Button
              icon={PrimeIcons.ANGLE_RIGHT}
              className="p-button-rounded p-button-text height-px-28 width-px-28"
              severity="secondary"
              disabled={numberAnswerSelected + 1 === lenghtAnswers}
              onClick={() => callback(numberAnswerSelected + 1)}
            />
          </div>
        )}
        {showActions && (
          <div className="flex-container align-center action-answer">
            {items.map(
              (item, index) =>
                item && (
                  <div key={`action-answer-${index}`}>
                    <Tooltip
                      target={`.button-name`}
                      position="bottom"
                      mouseTrack
                      mouseTrackLeft={10}
                    />
                    <Button
                      icon={
                        item.name === labelForward ? (
                          <Image src={item.icon} alt={item.name} />
                        ) : (
                          item.icon
                        )
                      }
                      className="p-button-rounded p-button-text prova button-name width-px-28 height-px-28"
                      onClick={item.command}
                      data-pr-tooltip={item.name}
                    />
                  </div>
                )
            )}
          </div>
        )}
      </div>
      <ModalSendAnswerFeedback
        showModal={showFeedbackModal}
        closeModalSendFeedbackCallback={() => setShowFeedbackModal(false)}
        projectId={projectId}
        questionId={questionId}
        answerId={answer?.id}
      />
    </>
  );
};
